import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

export const useOnlineUsers = (user) => {
  const [onlineUsers, setOnlineUsers] = useState([]);
  const subscriptionRef = useRef(null);
  const heartbeatRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const inactivityCheckRef = useRef(null);

  // 更新最後活動時間
  const updateActivity = () => {
    const now = Date.now();
    lastActivityRef.current = now;
    console.log('🔄 Activity updated for user:', user?.nickname, 'at', new Date(now).toLocaleTimeString());
  };

  // 強制登出函數
  const forceLogout = async () => {
    try {
      console.log('⚠️ Forcing logout due to inactivity for user:', user?.nickname);
      
      // 清理用戶數據
      if (user?.id) {
        const { error } = await supabase
          .from('online_users')
          .delete()
          .eq('id', user.id);
        
        if (error) {
          console.error('❌ Error removing user during logout:', error);
        } else {
          console.log('✅ User removed during logout:', user.nickname);
        }
      }
      
      // 清理 session storage
      sessionStorage.removeItem('chatroom-nickname');
      sessionStorage.removeItem('chatroom-user-id');
      
      // 重新載入頁面回到登入畫面
      window.location.reload();
    } catch (error) {
      console.error('❌ Error during force logout:', error);
      // 即使出錯也要重新載入
      window.location.reload();
    }
  };

  useEffect(() => {
    if (!user?.id || !user?.nickname) {
      console.log('⚠️ User not ready for online tracking:', { id: user?.id, nickname: user?.nickname });
      return;
    }

    console.log('🚀 Starting online user tracking for:', user.nickname, 'with ID:', user.id);

    const joinRoom = async () => {
      try {
        console.log('📝 Attempting to join room as:', user.nickname);
        
        // 檢查是否已經存在相同的用戶
        const { data: existingUsers, error: checkError } = await supabase
          .from('online_users')
          .select('*')
          .eq('id', user.id);

        if (checkError) {
          console.error('❌ Error checking existing users:', checkError);
        } else {
          console.log('👥 Existing users with same ID:', existingUsers);
        }

        const currentUser = {
          id: user.id,
          nickname: user.nickname,
          connected_at: new Date().toISOString(),
          last_activity: new Date().toISOString()
        };

        console.log('📤 Inserting/updating user with data:', currentUser);

        const { data, error } = await supabase
          .from('online_users')
          .upsert([currentUser], { 
            onConflict: 'id',
            ignoreDuplicates: false 
          })
          .select();

        if (error) {
          console.error('❌ Failed to join room:', error);
          console.error('Error details:', error.message, error.details, error.hint);
          console.error('Error code:', error.code);
        } else {
          console.log('✅ Successfully joined room:', data);
          // 立即獲取用戶列表
          await fetchOnlineUsers();
        }
      } catch (error) {
        console.error('❌ Exception in joinRoom:', error);
      }
    };

    const fetchOnlineUsers = async () => {
      try {
        console.log('🔍 Fetching online users...');
        
        const { data: allUsers, error: fetchError } = await supabase
          .from('online_users')
          .select('*')
          .order('connected_at', { ascending: true });

        if (fetchError) {
          console.error('❌ Failed to fetch online users:', fetchError);
          setOnlineUsers([]);
          return;
        }

        console.log('📊 Raw users from database:', allUsers);
        console.log('📊 Total users count:', allUsers?.length || 0);

        // 檢查數據質量
        const validUsers = allUsers?.filter(u => u.id && u.nickname) || [];
        const invalidUsers = allUsers?.filter(u => !u.id || !u.nickname) || [];

        if (invalidUsers.length > 0) {
          console.warn('⚠️ Found invalid users:', invalidUsers);
        }

        console.log('✅ Valid users:', validUsers.map(u => ({ id: u.id, nickname: u.nickname, connected_at: u.connected_at })));
        
        setOnlineUsers(validUsers);
      } catch (error) {
        console.error('❌ Exception in fetchOnlineUsers:', error);
        setOnlineUsers([]);
      }
    };

    const setupSubscription = () => {
      try {
        console.log('🔗 Setting up realtime subscription for online users...');
        
        if (subscriptionRef.current) {
          console.log('🔄 Unsubscribing from previous subscription...');
          subscriptionRef.current.unsubscribe();
        }
        
        const channel = supabase
          .channel('online-users-realtime-channel')
          .on('postgres_changes', 
            {
              event: 'INSERT',
              schema: 'public',
              table: 'online_users'
            }, 
            (payload) => {
              console.log('➕ User joined (INSERT):', payload.new);
              setOnlineUsers(prev => {
                const exists = prev.some(u => u.id === payload.new.id);
                if (exists) {
                  console.log('🔄 User already exists, updating:', payload.new.nickname);
                  return prev.map(u => u.id === payload.new.id ? payload.new : u);
                } else {
                  console.log('✅ Adding new user:', payload.new.nickname);
                  return [...prev, payload.new];
                }
              });
            }
          )
          .on('postgres_changes', 
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'online_users'
            }, 
            (payload) => {
              console.log('🔄 User updated (UPDATE):', payload.new);
              setOnlineUsers(prev => 
                prev.map(u => u.id === payload.new.id ? payload.new : u)
              );
            }
          )
          .on('postgres_changes', 
            {
              event: 'DELETE',
              schema: 'public',
              table: 'online_users'
            }, 
            (payload) => {
              console.log('➖ User left (DELETE):', payload.old);
              setOnlineUsers(prev => 
                prev.filter(u => u.id !== payload.old.id)
              );
            }
          );

        subscriptionRef.current = channel;
        
        channel.subscribe((status) => {
          console.log('📡 Online users subscription status:', status);
          if (status === 'SUBSCRIBED') {
            console.log('✅ Successfully subscribed to online users changes');
          } else if (status === 'CHANNEL_ERROR') {
            console.error('❌ Channel error in online users subscription');
          }
        });
          
        console.log('✅ Online users subscription set up successfully');
      } catch (error) {
        console.error('❌ Online users subscription setup error:', error);
      }
    };

    const setupHeartbeat = () => {
      console.log('💓 Setting up heartbeat for user:', user.nickname);
      
      heartbeatRef.current = setInterval(async () => {
        try {
          const now = Date.now();
          const lastActivity = lastActivityRef.current;
          const timeSinceActivity = now - lastActivity;
          
          console.log('💓 Heartbeat check for user:', user.nickname, 'Time since activity:', Math.round(timeSinceActivity / 1000), 'seconds');
          
          // 檢查是否超過10分鐘無活動
          if (timeSinceActivity > 10 * 60 * 1000) {
            console.log('⏰ User inactive for 10 minutes, logging out...', user.nickname);
            forceLogout();
            return;
          }
          
          const currentUser = {
            id: user.id,
            nickname: user.nickname,
            connected_at: new Date().toISOString(),
            last_activity: new Date(lastActivity).toISOString()
          };
          
          console.log('📤 Sending heartbeat:', currentUser);
          
          const { data, error } = await supabase
            .from('online_users')
            .upsert([currentUser], { 
              onConflict: 'id',
              ignoreDuplicates: false 
            })
            .select();
            
          if (error) {
            console.error('❌ Heartbeat error:', error);
            console.error('Heartbeat error details:', error.message, error.details, error.hint);
          } else {
            console.log('✅ Heartbeat sent successfully:', data);
          }
        } catch (error) {
          console.error('❌ Exception in heartbeat:', error);
        }
      }, 30000); // 每30秒發送心跳
    };

    // 設置閒置檢查
    const setupInactivityCheck = () => {
      console.log('⏰ Setting up inactivity check for user:', user.nickname);
      
      inactivityCheckRef.current = setInterval(() => {
        const now = Date.now();
        const lastActivity = lastActivityRef.current;
        const timeSinceActivity = now - lastActivity;
        
        console.log('⏰ Inactivity check for user:', user.nickname, 'Time since activity:', Math.round(timeSinceActivity / 1000), 'seconds');
        
        // 10分鐘無活動才登出
        if (timeSinceActivity > 10 * 60 * 1000) {
          console.log('⏰ Inactivity detected, forcing logout...', user.nickname);
          forceLogout();
        }
      }, 2 * 60 * 1000); // 每2分鐘檢查一次
    };

    const cleanup = async () => {
      try {
        console.log('🧹 Cleaning up user from online users:', user.nickname);
        
        const { error } = await supabase
          .from('online_users')
          .delete()
          .eq('id', user.id);
          
        if (error) {
          console.error('❌ Error removing user during cleanup:', error);
        } else {
          console.log('✅ User removed during cleanup:', user.nickname);
        }
      } catch (error) {
        console.error('❌ Exception in cleanup:', error);
      }
    };

    // 監聽用戶活動
    const handleUserActivity = () => {
      updateActivity();
    };

    // 添加活動監聽器
    const events = ['mousemove', 'keypress', 'click', 'scroll', 'touchstart', 'mousedown'];
    console.log('👂 Setting up activity listeners for events:', events);
    
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });

    // 頁面可見性變化監聽
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('👀 Page became visible, updating activity');
        updateActivity();
      } else {
        console.log('🙈 Page became hidden');
      }
    };

    // 頁面卸載監聽
    const handleBeforeUnload = () => {
      console.log('🚪 Page unloading, cleaning up user:', user.nickname);
      cleanup();
    };

    // 初始化所有功能
    console.log('🎯 Initializing online user system...');
    joinRoom();
    setupSubscription();
    setupHeartbeat();
    setupInactivityCheck();

    // 添加事件監聽器
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 清理函數
    return () => {
      console.log('🧹 Cleaning up online user system for:', user.nickname);
      
      cleanup();
      
      // 清理所有監聽器
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
      
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (subscriptionRef.current) {
        try {
          console.log('📡 Unsubscribing from online users channel...');
          subscriptionRef.current.unsubscribe();
        } catch (error) {
          console.warn('⚠️ Online users unsubscribe error:', error);
        }
      }
      
      if (heartbeatRef.current) {
        console.log('💓 Clearing heartbeat interval...');
        clearInterval(heartbeatRef.current);
      }
      
      if (inactivityCheckRef.current) {
        console.log('⏰ Clearing inactivity check interval...');
        clearInterval(inactivityCheckRef.current);
      }
    };
  }, [user?.id, user?.nickname]);

  // 調試用：監聽狀態變化
  useEffect(() => {
    console.log('📊 Online users state updated:', {
      count: onlineUsers.length,
      users: onlineUsers.map(u => ({ id: u.id, nickname: u.nickname }))
    });
  }, [onlineUsers]);

  return { onlineUsers, updateActivity };
};